import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import PohotovostService from "./PohotovostService";
import type { Pohotovost, PohotovostOdpoved, CreatePohotovostRequest } from "../generated/models/Pohotovost";

// -----------------------------
// POHOTOVOST QUERIES
// -----------------------------

export const usePohotovostVsechnyQuery = () => {
    return useQuery<Pohotovost[], Error>({
        queryKey: ['pohotovostVsechny'],
        queryFn: () => PohotovostService.getVsechny(),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
        refetchInterval: 30000,
    });
};

export const usePohotovostAktivniQuery = () => {
    return useQuery<Pohotovost[], Error>({
        queryKey: ['pohotovostAktivni'],
        queryFn: () => PohotovostService.getAktivni(),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
        refetchInterval: 30000,
    });
};

export const usePohotovostDetailQuery = (id: number | null) => {
    return useQuery<Pohotovost, Error>({
        queryKey: ['pohotovostDetail', id],
        queryFn: () => PohotovostService.getById(id!),
        enabled: id !== null,
    });
};

export const usePohotovostOdpovediQuery = (id: number | null) => {
    return useQuery<PohotovostOdpoved[], Error>({
        queryKey: ['pohotovostOdpovedi', id],
        queryFn: () => PohotovostService.getOdpovedi(id!),
        enabled: id !== null,
        refetchInterval: 10000,
    });
};

// -----------------------------
// POHOTOVOST MUTATIONS
// -----------------------------

export const useVytvorPohotovostMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<Pohotovost, Error, CreatePohotovostRequest>({
        mutationFn: (data) => PohotovostService.vytvorit(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pohotovostVsechny'] });
            queryClient.invalidateQueries({ queryKey: ['pohotovostAktivni'] });
            showToast('Pohotovostní výzva byla vytvořena.', 'success');
        },
        onError: (error) => {
            showToast(error.message || 'Chyba při vytváření výzvy.', 'error');
        },
    });
};

export const useUpravPohotovostMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, { id: number; data: CreatePohotovostRequest }>({
        mutationFn: ({ id, data }) => PohotovostService.upravit(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pohotovostVsechny'] });
            queryClient.invalidateQueries({ queryKey: ['pohotovostAktivni'] });
            showToast('Výzva byla upravena.', 'success');
        },
        onError: (error) => {
            showToast(error.message || 'Chyba při úpravě výzvy.', 'error');
        },
    });
};

export const useUzavritPohotovostMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (id) => PohotovostService.uzavrit(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pohotovostVsechny'] });
            queryClient.invalidateQueries({ queryKey: ['pohotovostAktivni'] });
            showToast('Výzva byla uzavřena.', 'success');
        },
        onError: (error) => {
            showToast(error.message || 'Chyba při uzavírání výzvy.', 'error');
        },
    });
};
